import { useState } from "react";
import { toast } from "sonner";
import { Btn } from "@/components/ui-kit";
import api from "@/lib/api";
import Modal from "@/pages/ops/Modal";
import ShiftCheck, { useShiftCheck } from "@/components/ShiftCheck";

function ChangeRequest({ cr, onApply, onDecline }) {
  if (!cr || cr.status !== "Pending") return null;
  return (
    <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-[12.5px] text-amber-900" data-testid="shift-change-request">
      <b>{cr.by || "Clinician"}</b> asked to move this to <b>{cr.date}</b> {cr.start}–{cr.end}{cr.reason ? ` — “${cr.reason}”` : ""}
      <div className="flex gap-2 mt-2">
        <Btn size="sm" variant="primary" onClick={onApply} data-testid="shift-change-apply">Use this date</Btn>
        <Btn size="sm" onClick={onDecline} data-testid="shift-change-decline">Decline</Btn>
      </div>
    </div>
  );
}

export default function ShiftEditModal({ shift, patients = [], onClose, onSaved }) {
  const [f, setF] = useState({
    date: shift.date || "", start: shift.start || "09:00", end: shift.end || "17:00",
    location: shift.location || "", note: shift.note || "", patient_id: shift.patient_id || "",
    clinician_id: shift.clinician_id,
  });
  const [newClin, setNewClin] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const check = useShiftCheck({ clinicianId: f.clinician_id, date: f.date, start: f.start, end: f.end, patientId: f.patient_id, sid: shift.id });
  const put = async (body, msg) => {
    setBusy(true);
    try { await api.put(`/ops/roster/${shift.id}`, body); toast.success(msg); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update shift"); }
    finally { setBusy(false); }
  };
  const save = () => (f.date ? put(f, newClin ? `Shift moved to ${newClin}` : "Shift updated") : toast.error("Pick a date"));
  const cr = shift.change_request;
  return (
    <Modal title="Edit shift" subtitle={`Clinician: ${newClin || shift.clinician_name || "—"}`} onClose={onClose}>
      <ChangeRequest cr={cr} onApply={() => setF({ ...f, date: cr.date, start: cr.start || f.start, end: cr.end || f.end })}
        onDecline={() => put({ resolve_request: "decline" }, "Request declined")} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block"><span className="pq-label">Date</span>
          <input type="date" className="pq-input mt-1" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="shift-edit-date" /></label>
        <label className="block"><span className="pq-label">Start</span>
          <input type="time" className="pq-input mt-1" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid="shift-edit-start" /></label>
        <label className="block"><span className="pq-label">End</span>
          <input type="time" className="pq-input mt-1" value={f.end} onChange={(e) => set("end", e.target.value)} data-testid="shift-edit-end" /></label>
      </div>
      <label className="block mt-3"><span className="pq-label">Location</span>
        <input className="pq-input mt-1" value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="shift-edit-location" /></label>
      <label className="block mt-3"><span className="pq-label">Patient</span>
        <select className="pq-input mt-1" value={f.patient_id} onChange={(e) => set("patient_id", e.target.value)} data-testid="shift-edit-patient">
          <option value="">No patient (general shift)</option>
          {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select></label>
      <ShiftCheck data={check} prefix="shift-edit-check"
        onPickDate={(d) => setF({ ...f, date: d.date, start: d.start, end: d.end })}
        onPickClinician={(c) => { setF({ ...f, clinician_id: c.id, date: c.date }); setNewClin(c.name); }} />
      <label className="block mt-3"><span className="pq-label">Note</span>
        <input className="pq-input mt-1" value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="shift-edit-note" /></label>
      <div className="flex gap-2 mt-5 justify-end">
        <Btn onClick={onClose} data-testid="shift-edit-cancel">Cancel</Btn>
        <Btn variant="primary" onClick={save} disabled={busy} data-testid="shift-edit-save">{busy ? "Saving…" : "Save shift"}</Btn>
      </div>
    </Modal>
  );
}
